/**
 * Custom hook for provisioning API calls
 */
import { useApi, discoveryApiRef } from '@backstage/core-plugin-api';
import { ProvisioningComponent } from '../types';

/**
 * Hook for making provisioning API calls
 * @param updateProvisioningComponent - Callback to update component status
 * @param setMutationError - Callback to set error state
 * @param setCurrentTaskId - Callback to set the current task ID for signal tracking
 * @param provisioningComponents - Current provisioning components to check completion status
 * @returns API call function
 */
export const useProvisioningAPI = (
  updateProvisioningComponent: (
    event: string,
    status: ProvisioningComponent['status'],
    progress: number,
  ) => void,
  setMutationError: (error: string | null) => void,
  setCurrentTaskId: (taskId: string | null) => void,
  provisioningComponents: ProvisioningComponent[],
) => {
  const discoveryApi = useApi(discoveryApiRef);

  /**
   * Creates application and starts bulk provisioning
   * @param name - Application name
   * @param description - Application description
   * @param _userLogin - User login (currently unused but kept for future use)
   */
  const createApplicationAndProvision = async (
    name: string,
    description: string,
    _userLogin: string,
  ) => {
    try {
      // Step 1: Mark application creation as starting (10% progress for user feedback)
      // Skip this if app is already completed (during retry)
      const appCreationComponent = provisioningComponents.find(
        c => c.id === 'app-creation',
      );
      if (appCreationComponent?.status !== 'completed') {
        updateProvisioningComponent('app-creation', 'in-progress', 10);
      }

      const baseUrl = await discoveryApi.getBaseUrl('experimental-apps');

      // Step 2: Start bulk provisioning
      const bulkResponse = await fetch(`${baseUrl}/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          description,
        }),
      });

      if (!bulkResponse.ok) {
        throw new Error(
          `Failed to start provisioning: ${bulkResponse.statusText}`,
        );
      }

      const bulkData = await bulkResponse.json();
      // eslint-disable-next-line no-console
      console.log('OnboardingModal: Bulk provisioning started:', bulkData);

      if (!bulkData.success || !bulkData.taskId) {
        throw new Error(
          bulkData.message || 'Failed to start bulk provisioning',
        );
      }

      // Step 3: Mark app creation as in-progress to show 10% progress
      // This provides immediate feedback that provisioning has started
      // Skip this if app is already completed (during retry)
      const appComponent = provisioningComponents.find(
        c => c.id === 'app-creation',
      );
      if (appComponent?.status !== 'completed') {
        updateProvisioningComponent('app-creation', 'in-progress', 0);
      }

      // Step 4: Set taskId to start listening for signals
      setCurrentTaskId(bulkData.taskId);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error in createApplicationAndProvision:', error);
      setMutationError(
        error instanceof Error
          ? error.message
          : 'Failed to create experimental application',
      );
      updateProvisioningComponent('app-creation', 'failed', 0);
      throw error;
    }
  };

  return {
    createApplicationAndProvision,
    retryProvisioning: createApplicationAndProvision,
  };
};
